import {
    Author,
    ColorFinderImage,
    Coordinate,
    ColorfinderImageBatch,
    Group,
    Hotspot,
    HotspotBatch,
    Icon,
    IconImage,
    ImageColorFinderFlight,
    InfraredFlight,
    InfraredImage,
    InfraredImageBatch,
    InfraredJob,
    Line,
    LiveVideoFlight,
    MapComment,
    MergedFlight,
    MessageFns,
    OrthoFlight,
    OrthoImage,
    OrthoImageBatch,
    OrthoJob,
    OrthoMask,
    PanoramaFlight,
    PanoramaImage,
    PDFJob,
    PDFRaster,
    Perimeter,
    PerimeterDistance,
    Project,
    PDFRaster as ProtobufPDFRaster,
    Ruler,
    SatelliteFlight,
    SatelliteJob,
    Text,
    UserLocationShare,
    VideoColorFinderFlight,
    VideoColorFinderJob,
    VideoFlight,
    VideoJob,
} from "@nova/protobuf"
import { ActionCreatorWithPayload, Dispatch, ThunkDispatch } from "@reduxjs/toolkit"
import { HTTPVersion, Instance } from "find-my-way"
import { z } from "zod"
export * as zIndex from "./zIndex"
export * from "./drone-catalog"
export * from "./aircraft-type-designators"
export * from "./airline-prefixes"
export * from "./weather"
import type * as React from "react"

/**
 * ID is just a utility type to make it clearer that something is intended to be an ID
 */
export type ID = string
/**
 * Email string type for member invitations, where a userId can be an email string or an ID string
 */
export type Email = string

export type Color = number

/**
 * UTM/ad-tracking parameters forwarded through the registration flow
 */
export type CampaignTrackingParameters = {
    utm_source: string
    utm_medium: string
    utm_campaign: string
    utm_content: string
    utm_term: string
    gclid: string
}

/**
 * Identifiable is the common structure for objects that are identifiable - i.e., they have an ID
 */
export type Identifiable = { id: ID }

export enum JobStatusType {
    QUEUED = 1,
    STARTED = 2,
    STOPPED = 3,
    FAILED = 4,
    COMPLETED = 5,
    MOCKCOMPLETED = 6,
    LIVE = 7,
}

export enum WorkspaceConfirmationModalName {
    DeleteWorkspace = "delete-workspace",
    DeleteFolder = "delete-folder",
    DeleteProject = "delete-project",
    LeaveWorkspace = "leave-workspace",
    RemoveCollaborators = "remove-collaborators",
    UnassignSubscription = "unassign-subscription",
    DisconnectIntegration = "disconnect-integration",
    DeleteHotspot = "delete-hotspot",
    DeleteMultipleHotspots = "delete-multiple-hotspots",
    DeleteColorFinderComment = "delete-color-finder-comment",
}

export enum TagModelTypes {
    Folder = "FOLDER",
    Group = "GROUP",
    IRD_Chapter = "IRD_CHAPTER",
    RGB_Chapter = "RGB_CHAPTER",
    VID_CHAPTER = "VID_CHAPTER",
    CF_CHAPTER = "CF_CHAPTER",
    PANO_CHAPTER = "PANO_CHAPTER",
    Chapter = "CHAPTER",
    //deprecated - use Folder instead
    Region = "REGION",
    Zone = "ZONE",
    Portfolio_Node = "PORTFOLIO_NODE",
    Portfolio_Leaf = "PORTFOLIO_LEAF",
}

export enum ImageTypes {
    Infrared = 1,
    Ortho = 2,
    ColorFinder = 3,
    Panorama = 4,
}

export enum RegistrationStartErrorType {
    invalidIp = "invalidIp",
    emailAlreadyRegistered = "emailAlreadyRegistered",
}

/**
 * This is a symbol that something is loading
 */
export const Loading = { loading: true }

export type LoadingErrorMaybe<T extends Identifiable, E = unknown> =
    | T
    | { id: ID; loading: boolean }
    | { id: ID; error: E }

export function isUsable<T extends Identifiable>(
    t: LoadingErrorMaybe<T> | undefined
): T | undefined {
    // check to see if we need to short-circuit - this could happen if someone has deleted it
    if (t === undefined || "loading" in t || "error" in t) {
        return undefined
    } else {
        return t as T
    }
}

export type InfraredImageWithContext = InfraredImage & {
    s3Key: string
    state: ImageProcessingState.IngestDone // Don't want user to hit "process" until all images have been uploaded AND ingested.
    fileName: string
    flightId: string
}

export type OrthoImageWithContext = OrthoImage & {
    s3Key: string
    state: ImageProcessingState.IngestDone // Don't want user to hit "process" until all images have been uploaded AND ingested.
}

export type ColorFinderImageWithContext = ColorFinderImage & {
    s3Key: string
    state: ImageProcessingState.UploadDone
}

export type PanoramaImageWithContext = PanoramaImage & {
    s3Key: string
    state: ImageProcessingState.IngestDone
}

export type IconImageWithContext = IconImage & {
    s3Key: string
    state: ImageProcessingState.IngestDone
}

type CommonImageKeys = Extract<
    Extract<
        Extract<keyof InfraredImageWithContext, keyof OrthoImageWithContext>,
        keyof ColorFinderImageWithContext
    >,
    keyof PanoramaImageWithContext
>

export type GenericUpload = {
    tags?: Partial<PreviouslyParsedTagValues>
    warnings?: ImageWarnings
    id: string
    state: ImageProcessingState
    takenAt?: number
    name?: string
    s3KeyLR?: string
    imageHeight?: number
}

export type GenericImage = Pick<InfraredImageWithContext, CommonImageKeys>

export type GenericJob = {
    id: ID
    status: JobStatusType
    createdAt: number
    updatedAt?: number
    mock?: boolean
    progress?: number
}

export type Warning = {
    status: string
    warning: string
    type?: Warnings
    select?: (uploads: GenericUpload[], setSelected: (uploads: GenericUpload[]) => void) => void
}
export enum Warnings {
    Message = "message",
    Error = "error",
    Warning = "warning",
}
export type ImageWarnings = Record<string, Warning>
export type WarningDefinitionData = Record<
    string,
    { type: Warnings; successMessage: string; name: string; successStatus: string }
>
export enum ImageProcessingState {
    Pending = 0,
    Processing = 1,
    ProcessingDone = 2,
    PendingUpload = 3,
    Uploading = 4,
    UploadDone = 5,
    IngestDone = 6,
}
export interface UploadPresentationOptions {
    completeStates: ImageProcessingState[]
    uploadingStates?: ImageProcessingState[]
}
export const ImageProcessingColors = {
    [Warnings.Message]: "#858C97",
    [Warnings.Warning]: "#F6C629",
    [Warnings.Error]: "#FA4F25",
    ["Complete"]: "#16C949",
    ["Selected"]: "#27BBFF",
    ["Uploading"]: "#A2E9B6",
    ["ProcessingDone"]: "#858C97",
    ["Pending"]: "#404044",
    ["Processing"]: "#404044",
}

export enum UploadFlowWarnings {
    Obliques = "Oblique Images",
    Location = "Location Anomaly",
    Zoom = "Zoom Images",
    IR = "IR Images",
    NoThermalData = "No Thermal Data",
    NoLocation = "No Location Data",
}

export const UploadFlowWarningColors: Record<UploadFlowWarnings, string> = {
    [UploadFlowWarnings.Obliques]: "#FF8F2C" /* orange-500 */,
    [UploadFlowWarnings.Location]: "#FA4F25" /* red-500 */,
    [UploadFlowWarnings.Zoom]: "#F6C629" /* yellow-500  */,
    [UploadFlowWarnings.IR]: "#F6C629" /* yellow-500  */,
    [UploadFlowWarnings.NoLocation]: "#FA4F25" /* red-500 */,
    [UploadFlowWarnings.NoThermalData]: "#FA4F25" /* red-500 */,
}

export type UploadStateSummary = {
    stateCount: Record<ImageProcessingState, number>
    uploadCount: number
    errorCount: number
    warnings: ImageWarnings
}

export const supportedImageTypes = new Set(["TIFF", "JPEG", "IRB"])

export type PreviouslyParsedTagValues = Record<string, Record<string, unknown>>

// LRFTargetDistance/LRFTargetAbsAlt presence indicates sensortool's height was
// LRF-derived rather than the RelativeAltitude tag or DEM-based AGL fallback.
export const hasLRFTags = (tags: Partial<PreviouslyParsedTagValues> | undefined): boolean => {
    const droneDji = tags?.["drone-dji"]
    return !!(droneDji?.LRFTargetDistance || droneDji?.LRFTargetAbsAlt)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux anyaction missing asyncthunks
export type AnyAction = any

export type AppDispatch = Dispatch<AnyAction> & ThunkDispatch<unknown, unknown, AnyAction>

export enum Permissions {
    AgoraWebhook = 16, // 10000
    SkydioWebhook = 8, // 01000
    Admin = 7, // 111
    Editor = 3, // 011
    Viewer = 1, // 001
    None = 0,
}

export const permissionLabels: Record<Permissions, string> = {
    [Permissions.Viewer]: "view",
    [Permissions.Editor]: "edit",
    [Permissions.Admin]: "manage",
    [Permissions.SkydioWebhook]: "access",
    [Permissions.AgoraWebhook]: "access",
    [Permissions.None]: "access",
}

export enum JobItemType {
    HotspotDetection = "HOTSPOT_DETECTION",
    OrthomosaicGeneration = "ORTHOMOSAIC_GENERATION",
    Video = "VIDEO",
}

//used for data residency
export enum SupportedRegions {
    USA = "us-west-1",
    Canada = "ca-central-1",
    Australia = "ap-southeast-2",
}

export const PRIMARY_REGION = SupportedRegions.Canada
export type ReplicaRegion = Exclude<SupportedRegions, SupportedRegions.Canada> & string
export const REPLICA_REGIONS: readonly ReplicaRegion[] = Object.values(SupportedRegions).filter(
    (r): r is ReplicaRegion => r !== PRIMARY_REGION
)

export const bucketSuffix = {
    [SupportedRegions.Canada]: "",
    [SupportedRegions.USA]: "-us-west-1",
    [SupportedRegions.Australia]: "-ap-southeast-2",
}

export const apiGatewayRegionPrefix: Record<SupportedRegions, string> = {
    [SupportedRegions.Canada]: "ca",
    [SupportedRegions.USA]: "us",
    [SupportedRegions.Australia]: "ap",
}

export const prettyRegionName: Record<SupportedRegions, string> = {
    [SupportedRegions.Canada]: "Canada",
    [SupportedRegions.USA]: "United States",
    [SupportedRegions.Australia]: "Australia",
}

// ISO-3166-1 alpha-2 country code, as returned by the geolocation route
export const countryCodeToSupportedRegion: Record<string, SupportedRegions | undefined> = {
    CA: SupportedRegions.Canada,
    US: SupportedRegions.USA,
    AU: SupportedRegions.Australia,
}

export type NovaEnvironment = "localhost" | "staging" | "preview" | "production" | "alpha"

export enum Environments {
    Production,
    Staging,
    Alpha,
    Preview,
    Localhost,
}

export enum FlightTypes {
    Infrared = 1,
    Ortho = 2,
    Video = 3,
    ColorFinder = 4,
    Panorama = 5,
}

export enum JobTypes {
    Infrared = 1,
    Ortho = 2,
    PDF = 3,
    Video = 4,
    ColorFinder = 5,
    Panorama = 6,
    OrthoUrban = 7,
    OrthoRapid = 8,
    InfraredRapid = 9,
    Satellite = 10,
    SatelliteVisual = 11,
    SatelliteInfrared = 12,
    SatelliteRadar = 13,
}

export enum OrthoProcessingMode {
    General = 1,
    Urban = 2,
    Rapid = 3,
}

export enum InfraredProcessingMode {
    General = 1,
    Rapid = 3,
}

export enum SatelliteSensorType {
    Visual = 0,
    Infrared = 1,
    Radar = 2,
}

// slug is the mosaic generator Lambda's wire contract - a display-label copy change must not affect it.
export const SATELLITE_SENSOR_TYPE_INFO: Record<
    SatelliteSensorType,
    { label: string; slug: "visual" | "infrared" | "radar" }
> = {
    [SatelliteSensorType.Visual]: { label: "Visual", slug: "visual" },
    [SatelliteSensorType.Infrared]: { label: "Infrared", slug: "infrared" },
    [SatelliteSensorType.Radar]: { label: "Radar", slug: "radar" },
}

// Fixed data-source regions the satellite mosaic Lambdas deploy to, unrelated to org residency.
export enum SatelliteMosaicLambdaRegion {
    Sentinel1 = "eu-central-1",
    Sentinel2 = "us-west-2",
}

export const SATELLITE_MOSAIC_LAMBDA_BY_SENSOR: Record<
    "visual" | "infrared" | "radar",
    { mosaicName: string; region: SatelliteMosaicLambdaRegion }
> = {
    visual: {
        mosaicName: "sentinel-2-mosaic",
        region: SatelliteMosaicLambdaRegion.Sentinel2,
    },
    infrared: {
        mosaicName: "sentinel-2-mosaic",
        region: SatelliteMosaicLambdaRegion.Sentinel2,
    },
    radar: {
        mosaicName: "sentinel-1-mosaic",
        region: SatelliteMosaicLambdaRegion.Sentinel1,
    },
}

export const SATELLITE_TEMPORAL_BIAS_VALUES = ["before", "after", "closest"] as const

export type SatelliteTemporalBias = (typeof SATELLITE_TEMPORAL_BIAS_VALUES)[number]

export const SATELLITE_TEMPORAL_BIAS_LABELS: Record<SatelliteTemporalBias, string> = {
    before: "Before",
    after: "After",
    closest: "Closest to",
}

export enum SatelliteJobErrorCode {
    Unknown = 1,
    NoDataFound = 2,
}

export const SATELLITE_JOB_ERROR_MESSAGES: Record<SatelliteJobErrorCode, string> = {
    [SatelliteJobErrorCode.Unknown]: "Satellite mosaic failed to generate.",
    [SatelliteJobErrorCode.NoDataFound]:
        "Failed to find cloudfree satellite scenes of your region, please choose a different date.",
}

export const getSatelliteJobErrorMessage = (code?: number | null): string =>
    SATELLITE_JOB_ERROR_MESSAGES[code as SatelliteJobErrorCode] ??
    SATELLITE_JOB_ERROR_MESSAGES[SatelliteJobErrorCode.Unknown]

// refers to the type of tag items stored in the dynamodb table and used in modeling both fe and bff code
export enum ChapterModelTypes {
    IRD_Chapter = "IRD_CHAPTER",
    RGB_Chapter = "RGB_CHAPTER",
    VID_CHAPTER = "VID_CHAPTER",
    CF_CHAPTER = "CF_CHAPTER",
    PANO_CHAPTER = "PANO_CHAPTER",
}

export enum ImageModelTypes {
    IRD_IMAGE = "IRD_IMAGE",
    RGB_IMAGE = "RGB_IMAGE",
    CF_IMAGE = "CF_IMAGE",
    PANO_IMAGE = "PANO_IMAGE",
}

export enum CubeItemType {
    FirePerimeter = "FIRE_PERIMETER",
    Icon = "ICON",
    InfraredImage = "INFRARED_IMAGE",
    Hotspot = "HOTSPOT",
    Line = "LINE",
    Text = "TEXT",
    PDFRaster = "PDF_RASTER",
}

export const ViewerSchema = z.object({
    id: z.string(),
    name: z.string(),
    userId: z.string(),
    projectId: z.string(),
    email: z.string(),
})

export type Viewer = z.infer<typeof ViewerSchema>

export const DroneSchema = z.object({
    flightId: z.string(),
    name: z.string(),
    projectId: z.string(),
    createdBy: z.object({
        userId: z.string(),
    }),
    startedAt: z.number(),
})
export type Drone = z.infer<typeof DroneSchema>

export type MouseType = {
    x: number
    y: number
}

export const defaultPrimaryColor = "#12393F"

export enum userColorPalette {
    red = "#F58F8F",
    orange = "#F0BA88",
    green = "#A0D592",
    purple = "#A79DE6",
    blue = "#A1DFF3",
}

export type Cursor = {
    userId: string
    name: string
    id: string
    mouse: {
        coordinates: [number, number]
        id?: string
    }
}

export type LiveChatPayload = {
    userId: string
    id: string
    message: string
    updatedAt: number
}

export type PartialViewState = {
    longitude: number
    latitude: number
    zoom: number
    pitch: number
    bearing: number
    width?: number
    height?: number
}

export type ToolAttributes = Line | Text | Icon

export const navbarHeight = 48
export const toolboxWidth = 48
export const sidePanelMinWidth = 244
export const sidePanelMaxWidth = 354
export const footerHeight = 42
export const topBorderWidth = 0
export const leftBorderWidth = 8
export const rightBorderWidth = 8
export const bottomBorderWidth = 8
export const tutorialDialogWidth = 350

export const PDFBarHeight = 60
export const PDFPanelWidth = 244

export type BoundingBox = {
    bottomRight: [number, number]
    topRight: [number, number]
    topLeft: [number, number]
    bottomLeft: [number, number]
    topMiddle: [number, number]
    leftMiddle: [number, number]
    rightMiddle: [number, number]
    bottomMiddle: [number, number]
    polygon: [[number, number], [number, number], [number, number], [number, number]]
    area: number
}
export type SimpleBoundingBox = {
    minLat: number
    maxLat: number
    minLng: number
    maxLng: number
}

export type Latitude = number
export type Longitude = number
export type Pixel = number

export type MapPosition = {
    lat: Latitude
    lng: Longitude
    x: Pixel
    y: Pixel
}

export const IconGhostId = "icon-ghost"

export enum WorkspaceDragAndDropTypes {
    Folder = "Folder",
    Project = "Project",
}

export enum GeometryType {
    Line = 1,
    Polygon = 2,
}

export enum LineStroke {
    normal = 1,
    dashed = 2,
    dotted = 3,
    squared = 4,
}

export enum LineWidth {
    normal = 1,
    thick = 2,
    none = 3,
}

export type LineProperties = {
    color: number
    outline: number
    fill: number
    stroke: LineStroke
    width: LineWidth
    url?: string
    polygonPattern?: string
}

/** @deprecated */
export type LinePropertiesV1 = {
    name?: string
    color?: string
    outline?: string
    fill?: string
    stroke?: string
    width?: string
    url?: string
    polygonPattern?: string
}

export type LineEntityType = {
    id: ID
    projectId: ID
    type: GeometryType
    coordinates: [Longitude, Latitude][]
    properties: LineProperties
    polygonHoles?: [Longitude, Latitude][][]
    createdAt?: string
}

export type LayerGroup = {
    id: ID
    rootId: ID
    layerIds: ID[]
    fullName: string
    visible?: boolean
    createdAt?: string
}

export enum LayerIndex {
    Satellite = -110,
    SatelliteOverlap = -109,
    PDFRaster = -100,
    HeatOverlay = -98,
    FirePerimetersOverlay = -97,
    Ortho = -90,
    OrthoOverlap = -85,
    IRImage = -80,
    OrthoBBOX = -75,
    OrthoComparisonBoost = -74,
    OrthoComparison = -73,
    WeatherHeatmap = -72,
    WeatherParticles = -71,
    Line = -70,
    LineBBOX = -65,
    FirePerimeterBuffers = -60,
    FirePerimeterBBOX = -55,
    FirePerimeter = -50,
    FirePerimeterPreview = -45,
    HotspotBBOX = -40,
    Ruler = -35,
    RulerBBOX = -30,
    Text = -25,
    GeometryBeingEdited = 20,
}

export const maxZoom = 25
export const minZoom = 4
export const minZoomOnTerrain = 10
export const maxZoomOnTerrain = 21

export type PDFTemplateData = {
    titles?: {
        title?: string
        subtitle?: string
    }
    compass?: boolean
    text1?: string
    projection?: string
    datum?: string
    createdAt?: string
    createdBy?: string
    operator?: string
    ratio?: string
    scaleBar?: boolean
    inset?: boolean
    qrcode?: boolean
    logos?: {
        left?: {
            id: string
        }
        right?: {
            id: string
        }
    }
    gridLines?: {
        x: number
        y: number
        offsetX: number
        offsetY: number
    }
    borderTicks?: {
        x: number
        y: number
        offsetX: number
        offsetY: number
    }
    pageSize?: PageSizes
    orientation?: string
    legendPosition?: string
    colorTheme?: string
    notation?: string
    coordinateSystem?: string
    hotspotsTable?: boolean
    useGoogleLinks?: boolean
}

export enum PageSizes {
    xs = 0,
    sm = 1,
    md = 2,
    lg = 3,
    xl = 4,
    xxl = 5,
}

export type OldText = {
    id: ID
    projectId: ID
    coordinates: { lng: number; lat: number }
    properties: TextProperties
    title?: string
    createdAt?: string
    deletedAt?: string
    visible?: boolean
}

export type TextProperties = {
    text: string
    rotation?: number
    //the original value we keep for scaling with the presets
    baseFontSize: number
    //the true value to be used in the layers and UI
    fontSize: number
    color: number
    bold?: boolean
}

export enum PanoramaView {
    Mini,
    Split,
    Full,
}

// For pubsub/sockets/jobs.ts and socket-events.ts
export type JobType = "pdf" | "infrared" | "orthomosaic"

export enum OrthoPalettes {
    WhiteHot = 1,
    Tint = 2,
    Fulgurite = 3,
    IronRed = 4,
    Medical = 5,
    BlackHot = 6,
    ScanArea = 7,
}

export enum HotspotStatus {
    Active = 1,
    Out = 2,
    Removed = 3,
}

export enum HotspotPointTypes {
    Point = 1,
    Heat = 2,
    Hex = 3,
    Polygon = 4,
    Label = 5,
}

/**
 * Hotspot Properties:
 * point color: string
 * poly fill: string
 * poly stroke: string
 * sensitivity: number
 * distance to perimeter: number[]
 */
export type HotspotProperties = {
    pointType?: HotspotPointTypes
    pointColor?: Color
    polyFill?: Color
    polyStroke?: Color
    orthoGradient?: OrthoPalettes
    sensitivity?: number
    perimeterDistance?: PerimeterDistance
}

export type HotspotWithContext = Hotspot & {
    flightId: string
    isManuallyUploaded: boolean
}

export type HotspotWithDistance = HotspotWithContext & {
    distanceToPerimeter: number | undefined
    color?: [number, number, number, number]
    sizeIndex: number
}

export type HotspotWithLabel = HotspotWithDistance & { label: string }

export type HotspotGraphData = {
    numberOfHotspotsPerBin: number[]
    leastHotspotsPerBar: number
    mostHotspotsPerBin: number
    barBounds: number[]
    absoluteGraphMinimum: number
    absoluteGraphMaximum: number
}

export type JobTypesWithProgressSupport = Extract<
    JobTypes,
    | JobTypes.Ortho
    | JobTypes.Infrared
    | JobTypes.PDF
    | JobTypes.OrthoUrban
    | JobTypes.OrthoRapid
    | JobTypes.InfraredRapid
    | JobTypes.Satellite
    | JobTypes.SatelliteVisual
    | JobTypes.SatelliteInfrared
    | JobTypes.SatelliteRadar
>
export const DURATION_PER_PERCENT_BY_STEP_PERCENT: Record<
    JobTypesWithProgressSupport,
    Record<number, number>
> = {
    [JobTypes.Ortho]: {
        0: 44,
        10: 1.5,
        50: 1,
        55: 4,
        65: 3.5,
        90: 4,
    },
    [JobTypes.OrthoUrban]: {
        0: 44,
        10: 4,
        35: 3,
        65: 9,
        75: 9,
        90: 5,
    },
    [JobTypes.OrthoRapid]: {
        0: 2,
        10: 1,
        30: 1,
        40: 2,
        50: 2,
        80: 4,
        95: 6,
    },
    [JobTypes.Infrared]: {
        0: 30,
        15: 8,
        40: 8,
        80: 4,
    },
    [JobTypes.InfraredRapid]: {
        0: 1,
        10: 0.5,
        30: 0.5,
        40: 1,
        50: 1,
        80: 2,
        95: 3,
    },
    [JobTypes.PDF]: {
        0: 1,
        10: 0.5,
        30: 1,
        40: 1,
        50: 1,
        80: 0.5,
        95: 1,
    },
    // sums to ~20s: 50*0.15 + 30*0.1 + 15*0.3 + 5*1 = 20
    [JobTypes.Satellite]: {
        0: 0.15,
        50: 0.1,
        80: 0.3,
        95: 1,
    },
    // Single step, no intermediate cap - the generator never reports progress to advance one.
    [JobTypes.SatelliteRadar]: { 0: 10 / 99 },
    [JobTypes.SatelliteInfrared]: { 0: 8 / 99 },
    [JobTypes.SatelliteVisual]: { 0: 12 / 99 },
}

export const SATELLITE_JOB_TYPE_BY_SENSOR_TYPE: Record<
    SatelliteSensorType,
    JobTypesWithProgressSupport
> = {
    [SatelliteSensorType.Visual]: JobTypes.SatelliteVisual,
    [SatelliteSensorType.Infrared]: JobTypes.SatelliteInfrared,
    [SatelliteSensorType.Radar]: JobTypes.SatelliteRadar,
}

export const SATELLITE_PROCESSING_MS_PER_KM2: Record<SatelliteSensorType, number> = {
    [SatelliteSensorType.Visual]: 65,
    [SatelliteSensorType.Infrared]: 45,
    [SatelliteSensorType.Radar]: 40,
}
export type Router = Instance<HTTPVersion.V1>

export const layerVisibilityLocalStorageKey = "hiddenLayers"

type IconComponent = React.FunctionComponent<React.SVGAttributes<SVGElement>>
export type UploadFlowProps = {
    portfolioId: string
    fireId: string
    id: string
    blurData: () => void
    processedActionHandler: {
        title: string
        handler: () => void
        icon: IconComponent
    }
    drawMode?: boolean
    uploadMode?: boolean
}

export type UploadComponentProps = UploadFlowProps

export type PDFRasterWithContext = ProtobufPDFRaster & {
    url: string
}

export enum SidePanelLayerType {
    Icon = "icon",
    Line = "line",
    Ruler = "ruler",
    Text = "text",
    FirePerimeter = "perimeter",
    PDFRaster = "pdf-raster",
    HotspotFlight = "hotspot",
    OrthomosaicFlight = "orthomosaic",
    VideoFlight = "video",
    VideoColorFinder = "video-colorfinder",
    ImageColorFinder = "image-colorfinder",
    Panorama = "panorama",
    Group = "group",
    LiveVideoFlight = "live-video-flight",
    MergedFlight = "merged-flight",
    PDF = "pdf",
    Comment = "comment",
    SatelliteFlight = "satellite",
    UserLocationShare = "user-location-share",
}

export type SidePanelLayer = (
    | Icon
    | Line
    | Ruler
    | Text
    | Perimeter
    | PDFRasterWithContext
    | InfraredFlight
    | OrthoFlight
    | VideoFlight
    | VideoColorFinderFlight
    | ImageColorFinderFlight
    | PanoramaFlight
    | LiveVideoFlight
    | MergedFlightWithContext
    | PDFJob
    | SatelliteFlight
    | UserLocationShare
) & {
    type: SidePanelLayerType
    TabComponent: ({ depth }: { depth: number }) => React.ReactElement
    // @deprecated
    DataComponent?: (props: UploadComponentProps) => React.ReactElement
    UploadComponent?: (props: UploadComponentProps) => React.ReactElement
}

/** `SidePanelLayer` but without the Tab or Data Components. */
export type SidePanelEntity = Omit<SidePanelLayer, "TabComponent" | "DataComponent">
export type SidePanelGroup = Group & { children: SidePanelLayerOrGroup[] }
export type SidePanelLayerOrGroup = Pick<
    SidePanelLayer,
    "createdAt" | "id" | "index" | "name" | "parentId"
> &
    Partial<SidePanelLayer> &
    Partial<SidePanelGroup>
export type AutoPerimeterEntity = Omit<SidePanelEntity, "type"> & { errorMessage?: string }

export type TabContainerProps<T> = {
    layer: T
    selected: boolean
    depth?: number
    canGroup: boolean
    onToggleVisibility?: (layer: SidePanelLayer, visible: boolean) => void
    onNameChange: (layer: SidePanelLayer, name: string) => void
    onSelectHandler: (event: React.MouseEvent, id: string) => void
    onClickHandler: (id: string) => void
    onContextMenu?: (event: React.MouseEvent, id: string) => void
    onCloseContextMenu: () => void
    onDeleteHandler?: (id: string) => void
    onUngroup?: () => void
    onGroup?: () => void
    onDrop?: (id: string, belowId?: string) => void
    onZoom?: (entity: T) => void
    DataComponent?: (props: UploadComponentProps) => React.ReactElement | null
    idRenaming?: string
    setIdRenaming: (id: string | undefined) => void
    allowDropAbove?: boolean
}

export type GroupTabContainerProps = {
    group: SidePanelGroup
    genericProps: GenericSidePanelProps
    selectedLayerIds: ID[]
    handleUngroupAll: (groupId: string) => void
    handleDelete: (id: string) => void
    handleGroupVisibilityToggle?: (group: SidePanelGroup, visible: boolean) => void
    onNameChange: (group: SidePanelGroup, name: string) => void
    handleTabClick: (groupId: string) => void
    handleTabSelect: (e: React.MouseEvent, groupId: string) => void
    handleContextMenu?: (event: React.MouseEvent, id: string) => void
    handleCloseContextMenu: () => void
    onDrop?: (id: string, belowId?: string) => void
    depth: number
    canGroup: boolean
    idRenaming?: string
    setIdRenaming: (id: string | undefined) => void
    allowDropAbove?: boolean
}

export type MergedFlightWithContext = MergedFlight & {
    hotspotFlightIds: ID[]
    hotspotFlights: InfraredFlight[]
    hotspots: Hotspot[]
}

export type MergedFlightContainerProps<T> = {
    mergedFlight: MergedFlightWithContext
    onZoom?: (entity: T) => void
}

export type GenericSidePanelProps = {
    layer: Omit<TabContainerProps<never>, "layer" | "selected" | "depth" | "onZoom">
    group: Omit<GroupTabContainerProps, "group" | "depth" | "genericProps">
}

export const defaultTextToolContent = "Add text"
export enum DefaultKmlShapeColors {
    stroke = "FF7083F0",
    fill = "7F4656A4",
}

/**
 * Almost every child of the Protobuf object is a list of layers
 * except for the ones listed here. This will allow the rest of
 * the layer arrays to be flattened and combined, omitting the
 * children listed here.
 */
export const ignoredProjectKeys = ["PDFs", "lastUpdated", "region", "id", "orgId", "orthoMasks"]
export const ignoredProjectKeysConst = [
    "PDFs",
    "lastUpdated",
    "region",
    "id",
    "orgId",
    "orthoMasks",
] as const satisfies (keyof ProjectWithMetadata)[]
export type IgnoredProjectKeysTuple = (typeof ignoredProjectKeysConst)[number]

export const UsageExceededErrorCode = 1005
export const UploadNetworkErrorCode = 1004

export type PreSignedPostCondition = ["content-length-range", number, number]

export const animation150 = "duration-150 ease-in-out"

export type KeyboardShortcut = {
    command?: boolean
    shift?: boolean
    option?: boolean
    backspace?: boolean
    enter?: boolean

    letters: string[]
}

/**
 * These values are what appear in the UI.
 */
export enum MapFeature {
    Labels = "Map Labels",
    Contours = "Contours",
    Shading = "Shading",
    Buildings = "Buildings",
    Terrain = "Terrain",
}

/**
 * These values are the actual substrings of the layer IDs/sources from useMapStyles
 * These values should be lowercase no matter what.
 * */
export const MapFeatureKeyByMapFeature: Record<MapFeature, string> = {
    [MapFeature.Labels]: "labels",
    [MapFeature.Buildings]: "building",
    [MapFeature.Contours]: "contours",
    [MapFeature.Shading]: "hillshade",
    [MapFeature.Terrain]: "terrain",
}

export const mapBaseLayerByKey: Record<string, string> = {
    light: "Default",
    satellite: "Satellite",
    white: "White",
    dark: "Dark",
}

export enum FileExtension {
    kml = "kml",
    kmz = "kmz",
    shp = "shp",
    pdf = "pdf",
    srt = "srt",
    gpx = "gpx",
    tiff = "tiff",
    tif = "tif",
    irb = "irb",
    xml = "xml",
    jpg = "jpg",
    jpeg = "jpeg",
    png = "png",
    MP4 = "mp4",
    p12 = "p12",
    csv = "csv",
    txt = "txt",
    owi = "owi",
    all = "*",
}

export const allowedFileExtensions: Record<string, FileExtension[]> = {
    FirePerimeter: [FileExtension.kml, FileExtension.kmz, FileExtension.srt, FileExtension.gpx],
}

export enum IconPackOption {
    Shape = "Shape",
    Number = "Numbers",
    NWCG = "NWCG - PMS 936",
    NFPAEmergency = "NFPA - Emergency Management",
    NFPAFireService = "NFPA - Fire Service",
    NFPAGeneralUse = "NFPA - General Use",
    SARCOP = "SARCOP",
    BCWS = "British Columbia Wildfire Service",
    Forestry = "Forestry",
    FireAI = "Fire AI",
}

export const IconPackOptionLabels = Object.values(IconPackOption)

export enum IconShape {
    Circle = "shape/circle",
    Square = "shape/square",
    Triangle = "shape/triangle",
    Star = "shape/star",
    Kite = "shape/kite",
    Plus = "shape/plus",
}

export type UpdateAction = ActionCreatorWithPayload<
    {
        id: string
        updates: Record<string, unknown>
    },
    string
>

export type DeleteAction = ActionCreatorWithPayload<
    {
        id: string
    },
    string
>

export enum ColorSwatchCategory {
    Icon,
    LineColor,
    PolygonOutline,
    PolygonFill,
    Text,
    TextOutline,
    Hotspot,
    FirePerimeterOutline,
    FirePerimeterBuffer,
    ScanAreaFill,
    ScanAreaOutline,
    NumberIcon,
}
export const hexColorSwatches: Record<ColorSwatchCategory, string[]> = {
    [ColorSwatchCategory.Icon]: ["#E34752", "#F07D2C", "#F2CB52", "#86DC8C", "#3EA89B"],
    [ColorSwatchCategory.NumberIcon]: ["#000000", "#FFFFFF", "#E34752", "#F07D2C", "#1E6DA9"],
    [ColorSwatchCategory.LineColor]: ["#7183F0", "#E34752", "#F07D2C", "#86DC8C", "#3EA89B"],
    [ColorSwatchCategory.PolygonOutline]: ["#7183F0", "#E34752", "#F07D2C", "#86DC8C", "#3EA89B"],
    [ColorSwatchCategory.PolygonFill]: ["#7183F040", "#E3475240", "#F07D2C40", "#86DC8C40"],
    [ColorSwatchCategory.Text]: ["#000000", "#FFFFFF", "#E34752", "#F07D2C", "#1E6DA9"],
    [ColorSwatchCategory.TextOutline]: ["#000000", "#FFFFFF", "#E34752", "#F07D2C", "#1E6DA9"],
    [ColorSwatchCategory.Hotspot]: ["#FF0000", "#FF6900", "#FFFF00", "#00FF2F", "#0057FF"],
    [ColorSwatchCategory.FirePerimeterOutline]: [
        "#E51A1A",
        "#25E223",
        "#486CEB",
        "#ED821E",
        "#8514F7",
    ],
    [ColorSwatchCategory.FirePerimeterBuffer]: [
        "#ED821E",
        "#F7EE15",
        "#486CEB",
        "#25E223",
        "#8514F7",
    ],
    [ColorSwatchCategory.ScanAreaFill]: [
        "#DCA33240",
        "#32ACDC40",
        "#32DC3540",
        "#E1E81540",
        "#DC323240",
    ],
    [ColorSwatchCategory.ScanAreaOutline]: ["#DCA332", "#32ACDC", "#32DC35", "#E1E815", "#DC3232"],
}

export const scanAreaDefaultHexFillColor = "#DCA33201" // Use 01 for alpha here to prevent it from being decoded as FF alpha
export const scanAreaDefaultHexOutlineColor = "#DCA332"

export const rulerOutlineColor = "#F0C570"
export const rulerFillColor = "#F0C57028"

export const lineStrokeNameByLineStroke: Record<LineStroke, string> = {
    [LineStroke.normal]: "Solid",
    [LineStroke.dashed]: "Rounded Dash",
    [LineStroke.dotted]: "Dotted",
    [LineStroke.squared]: "Squared Dash",
}

export const lineWidthNameByLineWidth: Record<LineWidth, string> = {
    [LineWidth.none]: "None",
    [LineWidth.normal]: "Thin",
    [LineWidth.thick]: "Thick",
}

export const PUBSUB_CURSOR_THROTTLE_MS = 50

export enum InspectorPanelMode {
    Layer,
    Data,
}

export type EmailList = {
    id: ID
    name: string
    emails: string[]
    workspaceId: ID
}
export const LIVE_VIDEO_EXPIRATION_TIME = 1000 * 60 * 10

export enum DistanceUnit {
    Meters = "meters",
    Yards = "yards",
    NauticalMiles = "nauticalmiles",
    Feet = "feet",
    Kilometers = "kilometers",
    Miles = "miles",
}

export enum AreaUnit {
    Acres = "acres",
    Hectares = "hectares",
    SquareKilometers = "kilometers",
    SquareMiles = "miles",
    SquareFeet = "sqfeet",
    SquareMeters = "sqmeters",
}

export const formattedDistanceUnit: Record<DistanceUnit, { abbrev: string; full: string }> = {
    [DistanceUnit.Feet]: { abbrev: "ft", full: "Feet (ft)" },
    [DistanceUnit.Miles]: { abbrev: "mi", full: "Miles (mi)" },
    [DistanceUnit.Meters]: { abbrev: "m", full: "Meters (m)" },
    [DistanceUnit.Kilometers]: { abbrev: "km", full: "Kilometers (km)" },
    [DistanceUnit.NauticalMiles]: { abbrev: "NM", full: "Nautical Miles (NM)" },
    [DistanceUnit.Yards]: { abbrev: "yd", full: "Yards (yd)" },
}

export const formattedAreaUnit: Record<AreaUnit, { abbrev: string; full: string }> = {
    [AreaUnit.SquareFeet]: { abbrev: "ft²", full: "Feet (ft²)" },
    [AreaUnit.SquareMeters]: { abbrev: "m²", full: "Meters (m²)" },
    [AreaUnit.SquareMiles]: { abbrev: "mi²", full: "Miles (mi²)" },
    [AreaUnit.SquareKilometers]: { abbrev: "km²", full: "Kilometers (km²)" },
    [AreaUnit.Acres]: { abbrev: "ac", full: "Acres (ac)" },
    [AreaUnit.Hectares]: { abbrev: "ha", full: "Hectares (ha)" },
}

// Metric distance is represented as `Meters` (the "m/km" setting option); `Kilometers`
// is not a valid org distance setting and would render as an empty dropdown selection.
export const defaultUnitsForRegion: Record<
    SupportedRegions,
    { distance: DistanceUnit; area: AreaUnit }
> = {
    [SupportedRegions.USA]: { distance: DistanceUnit.Feet, area: AreaUnit.Acres },
    [SupportedRegions.Canada]: { distance: DistanceUnit.Meters, area: AreaUnit.Hectares },
    [SupportedRegions.Australia]: { distance: DistanceUnit.Meters, area: AreaUnit.Hectares },
}

export const hotspotSizes = [2, 6, 8, 10, 16]
export const defaultHotspotSizeIndex = 2
export const hotspotPointFillOpacity = 0.6
export const hotspotPointFillOpacity8Bit = Math.round(hotspotPointFillOpacity * 255)

export type Integration = {
    updatedAt: number
    updatedBy: Author
}

export type ProjectWithMetadata = Project & { region: SupportedRegions; orgId: string; id: string }

export enum ImageType {
    JPG = 1,
    JPEG = 2,
    TIFF = 3,
    TIF = 4,
    IRB = 5,
    XML = 6,
    PNG = 7,
    OWI = 8,
}

export enum FirePerimeterMethod {
    None,
    Upload,
    Manual,
    Auto,
}

export enum ImagePreviewType {
    Infrared = "infrared",
    Ortho = "ortho",
    Colorfinder = "colorfinder",
    PDF = "pdf",
    Hotspot = "hotspot",
    Icon = "icon",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ProtobufEntity = MessageFns<any>

// "none" is for project entities that are neither a renderable layer nor a selectable entity (e.g. PDFs, groups, orthoMasks)
export type ProjectEntityKind = "layer" | "entity" | "none"

// keys of ProjectEntityMapEntry's optional sub-entity fields, mapped conditionally below based on which ones exist on E
type SubEntityKey = "job" | "images" | "hotspots"

type SubEntityValue<F> = NonNullable<F> extends (infer T)[] ? T : NonNullable<F>

type ProjectEntityMapEntry<E> = {
    entity: MessageFns<E>
    kind: ProjectEntityKind
} & {
    [K in SubEntityKey as K extends keyof E ? K : never]: {
        entity: MessageFns<SubEntityValue<E[K & keyof E]>>
        batch?: ProtobufEntity
    }
}
export const mapBaseLayerStorageKey = "map_baselayer"

//we needed a quick fix to apply some rotation to specific icons
//these icon urls are mapped to hardcoded rotation values and are applied in various spaces across the app
//eventually, the icon tool will be reworked and user defined rotation will be introduced
//at this point wherever the hardcoded values are passed, we would just need to pass the user defined value instead
export const rotatedIconsHardCodedValues: Record<string, number> = {
    "nwcg_assignment_break/branch_break_2": 90,
    "nwcg_assignment_break/branch_break_3": 315,
    "nwcg_assignment_break/branch_break_4": 45,
    "nwcg_assignment_break/division_break_2": 90,
    "nwcg_assignment_break/division_break_3": 315,
    "nwcg_assignment_break/division_break_4": 45,
    "nwcg_assignment_break/zone_break_2": 90,
    "nwcg_assignment_break/zone_break_3": 315,
    "nwcg_assignment_break/zone_break_4": 45,
}
export const getRotatedIconsHardCodedValues = (url: string): number => {
    const rotation = rotatedIconsHardCodedValues[url]
    if (!!rotation) return rotation
    return 0
}

export interface BannerConfig extends Record<string, unknown> {
    start: number
    end: number
    message: string
}

export const projectEntityMap = {
    infraredFlights: {
        entity: InfraredFlight,
        kind: "layer",
        job: { entity: InfraredJob },
        images: { entity: InfraredImage, batch: InfraredImageBatch },
        hotspots: { entity: Hotspot, batch: HotspotBatch },
    },
    orthoFlights: {
        entity: OrthoFlight,
        kind: "layer",
        job: { entity: OrthoJob },
        images: { entity: OrthoImage, batch: OrthoImageBatch },
    },
    videoFlights: {
        entity: VideoFlight,
        kind: "layer",
        job: { entity: VideoJob },
    },
    videoColorFinderFlights: {
        entity: VideoColorFinderFlight,
        kind: "layer",
        job: { entity: VideoColorFinderJob },
    },
    imageColorFinderFlights: {
        entity: ImageColorFinderFlight,
        kind: "layer",
        images: { entity: ColorFinderImage, batch: ColorfinderImageBatch },
    },
    panoramaFlights: {
        entity: PanoramaFlight,
        kind: "layer",
        images: { entity: PanoramaImage },
    },
    icons: { entity: Icon, kind: "entity", images: { entity: IconImage } },
    lines: { entity: Line, kind: "entity" },
    texts: { entity: Text, kind: "entity" },
    perimeters: { entity: Perimeter, kind: "entity" },
    PDFRasters: { entity: PDFRaster, kind: "layer" },
    PDFs: { entity: PDFJob, kind: "none" },
    groups: { entity: Group, kind: "none" },
    liveVideoFlights: { entity: LiveVideoFlight, kind: "layer" },
    mergedFlights: { entity: MergedFlight, kind: "layer" },
    rulers: { entity: Ruler, kind: "entity" },
    orthoMasks: { entity: OrthoMask, kind: "none" },
    mapComments: { entity: MapComment, kind: "entity" },
    satelliteFlights: {
        entity: SatelliteFlight,
        kind: "layer",
        job: { entity: SatelliteJob },
    },
    userLocationShares: { entity: UserLocationShare, kind: "layer" },
} satisfies {
    [K in keyof Project as Project[K] extends unknown[] ? K : never]: ProjectEntityMapEntry<
        Project[K][number]
    >
}

export const projectEntityKeys = Object.keys(projectEntityMap) as (keyof typeof projectEntityMap)[]

export type InfraredFlightData = Omit<InfraredFlight, "index" | "parentId" | "mergeId">
export const getDomainPrefix = (env: string) => {
    if (env === "production") return "app."
    else return env + "."
}

export const INTERNAL_EMAIL_DOMAINS = [
    "hummingbirddrones.ca",
    "mapnova.com",
    "flysmarttraining.ca",
    "fireai.io",
]
export const isInternalEmail = (email?: string | null): boolean => {
    const normalized = email?.toLowerCase()
    return (
        !!normalized && INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith("@" + domain))
    )
}

export enum GroupType {
    Layer = 0, // IMPORTANT: layer groups currently do not have their type set to 1, so check if a group is a layer type by doing !group.type
    PDF = 1,
}
export type Coordinate3D = {
    longitude: number
    latitude: number
    altitude: number
}

export enum IntegrationExportEntity {
    InfraredFlight = "infraredFlight",
    OrthoFlight = "orthoFlight",
    Perimeter = "perimeter",
    Icon = "icon",
    Line = "line",
}

export enum IntterraImageProduct {
    ElectroOpticalColour = "EOimage",
    IR = "IRimage",
    HeatSource = "HSimage",
}

export enum SampleDataFlightKey {
    ortho1 = "ortho1",
    ortho2 = "ortho2",
    vcf1 = "vcf1",
    vcf2 = "vcf2",
    ir1 = "ir1",
    ir2 = "ir2",
    pano1 = "pano1",
    fmv1 = "fmv1",
}

// This is for ca-central-1 localhost
export const sampleDataIds: {
    flightIds: Record<SampleDataFlightKey, string>
} = {
    flightIds: {
        [SampleDataFlightKey.ortho1]: "f530d715-5b2e-49cf-bcb0-72f216843e6f",
        [SampleDataFlightKey.ortho2]: "e8d07a22-5c56-4f46-8aae-26502a034068", // Same data as ortho1 but updated to have scan area 2.0
        [SampleDataFlightKey.vcf1]: "734716be-025f-429d-981a-566f731a5196",
        [SampleDataFlightKey.vcf2]: "a41d774a-fff4-4133-8721-da18ce9e9025",
        [SampleDataFlightKey.ir1]: "74129665-8015-4261-b37a-2d59f71080e9",
        [SampleDataFlightKey.ir2]: "9fce049e-2877-470b-acae-b46db3f976ad", // Same data as ir1 but updated to have scan area 2.0
        [SampleDataFlightKey.pano1]: "7f38cf44-7a4f-4562-9eeb-ef5402aff4c6",
        [SampleDataFlightKey.fmv1]: "beb61709-1f90-450a-9d78-082472e314e9",
    },
}

export const liveVideoTrialDuration = 86400

export enum BufferError {
    DistanceTooLarge = "distance_too_large",
    Unknown = "unknown",
}
export enum ToolName {
    Icon = "Icon",
    Ruler = "Ruler",
    Line = "Line",
    Text = "Text",
    Upload = "Upload",
    Create = "Create", // general add-on create menu
    PDF = "Generate PDF",
    FirePerimeter = "fire-perimeter", // excluded from top bar menu
    OrthoMask = "ortho-mask", // excluded from top bar menu
    Comment = "Comment",
}

export const ToolNameToLayerType: Record<ToolName, SidePanelLayerType | undefined> = {
    [ToolName.Icon]: SidePanelLayerType.Icon,
    [ToolName.Ruler]: SidePanelLayerType.Ruler,
    [ToolName.Line]: SidePanelLayerType.Line,
    [ToolName.Text]: SidePanelLayerType.Text,
    [ToolName.Upload]: undefined,
    [ToolName.Create]: undefined,
    [ToolName.PDF]: undefined,
    [ToolName.FirePerimeter]: SidePanelLayerType.FirePerimeter,
    [ToolName.OrthoMask]: undefined,
    [ToolName.Comment]: SidePanelLayerType.Comment,
}

export const jobTypeBySidePanelLayerType: Record<
    SidePanelLayerType,
    JobTypesWithProgressSupport | undefined
> = {
    [SidePanelLayerType.Icon]: undefined,
    [SidePanelLayerType.Line]: undefined,
    [SidePanelLayerType.Ruler]: undefined,
    [SidePanelLayerType.Text]: undefined,
    [SidePanelLayerType.FirePerimeter]: undefined,
    [SidePanelLayerType.PDFRaster]: undefined,
    [SidePanelLayerType.HotspotFlight]: JobTypes.Infrared,
    [SidePanelLayerType.OrthomosaicFlight]: JobTypes.Ortho,
    [SidePanelLayerType.VideoFlight]: undefined,
    [SidePanelLayerType.VideoColorFinder]: undefined,
    [SidePanelLayerType.ImageColorFinder]: undefined,
    [SidePanelLayerType.Panorama]: undefined,
    [SidePanelLayerType.Group]: undefined,
    [SidePanelLayerType.LiveVideoFlight]: undefined,
    [SidePanelLayerType.MergedFlight]: undefined,
    [SidePanelLayerType.PDF]: JobTypes.PDF,
    [SidePanelLayerType.Comment]: undefined,
    [SidePanelLayerType.SatelliteFlight]: JobTypes.Satellite,
    [SidePanelLayerType.UserLocationShare]: undefined,
}
export enum LiveImageProcessingState {
    Processing = 1,
    Processed = 2,
    Failed = 3,
}

export enum Licenses {
    Lite = "lite",
    Essentials = "essentials",
    Pro = "pro",
}

export const LicenseCapacitiesInBytes: Record<Licenses, number> = {
    [Licenses.Lite]: 2147483648,
    [Licenses.Essentials]: 107374182400,
    [Licenses.Pro]: Number.POSITIVE_INFINITY,
}

export enum AddOns {
    Hotspots = "hotspots",
    Colorfinder = "colorfinder",
    LiveVideo = "liveVideo",
}

export type SubscriptionsType = Licenses | AddOns

export enum BillingCycle {
    Year = "year",
    Month = "month",
}

export enum MemberType {
    Member = "member",
    Invite = "invite",
}

// Products that can receive push notifications (FCM token registration)
export enum NotifiableProduct {
    Atlas = "atlas",
}

export enum GeometryEditState {
    Normal = "normal",
    Editing = "editing",
}

export enum SkydioVideoType {
    Color = "color",
    Thermal = "thermal",
}

export const maxIconImagesCount = 5

export const concurrentArchiveCount = 5
export const concurrentUnarchiveCount = 5

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- no other way to make a wrapper function that knows the input is actually a function
export type AnyFunction = (...args: any[]) => any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyAsyncFunction = (...args: any[]) => Promise<any>

// TODO: @Thomas @Jarren -> Move to the Schemas package. Need this for filtering arrays where each item needs to match a zod schema.
export const safeParseArray = <T>(raw: string, schema: z.ZodSchema<T>): T[] => {
    try {
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed
            .map((item) => schema.safeParse(item))
            .filter((result) => result.success)
            .map((result) => result.data)
    } catch {
        return []
    }
}

export const possibleTimezones = [
    {
        id: "dateline",
        name: "(UTC-12:00) International Date Line West",
        iana: "Etc/GMT+12",
    },
    {
        id: "hst",
        name: "(UTC-10:00) Hawaii",
        iana: "Pacific/Honolulu",
    },
    {
        id: "akst",
        name: "(UTC-09:00) Alaska",
        iana: "America/Anchorage",
    },
    {
        id: "pst",
        name: "(UTC-08:00) Pacific Time (US & Canada)",
        iana: "America/Los_Angeles",
    },
    {
        id: "mst",
        name: "(UTC-07:00) Mountain Time (US & Canada)",
        iana: "America/Denver",
    },
    {
        id: "cst",
        name: "(UTC-06:00) Central Time (US & Canada)",
        iana: "America/Chicago",
    },
    {
        id: "est",
        name: "(UTC-05:00) Eastern Time (US & Canada)",
        iana: "America/New_York",
    },
    {
        id: "ast",
        name: "(UTC-04:00) Atlantic Time (Canada)",
        iana: "America/Halifax",
    },
    {
        id: "gmt",
        name: "(UTC+00:00) Dublin, Edinburgh, Lisbon, London",
        iana: "Europe/London",
    },
    {
        id: "cet",
        name: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris",
        iana: "Europe/Paris",
    },
    {
        id: "eet",
        name: "(UTC+02:00) Cairo, Helsinki, Kyiv, Riga",
        iana: "Europe/Helsinki",
    },
    {
        id: "msk",
        name: "(UTC+03:00) Moscow, St. Petersburg, Volgograd",
        iana: "Europe/Moscow",
    },
    {
        id: "ist",
        name: "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi",
        iana: "Asia/Kolkata",
    },
    {
        id: "cst_china",
        name: "(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi",
        iana: "Asia/Shanghai",
    },
    {
        id: "jst",
        name: "(UTC+09:00) Osaka, Sapporo, Tokyo",
        iana: "Asia/Tokyo",
    },
    {
        id: "aest",
        name: "(UTC+10:00) Canberra, Melbourne, Sydney",
        iana: "Australia/Sydney",
    },
    {
        id: "nzst",
        name: "(UTC+12:00) Auckland, Wellington",
        iana: "Pacific/Auckland",
    },
] as const
export type TimezoneId = (typeof possibleTimezones)[number]["id"]

export const AIRCRAFT_CONDITION_OPTIONS = [
    { label: "Good to fly", value: "good_to_fly" },
    { label: "Needs maintenance", value: "needs_maintenance" },
    { label: "Grounded", value: "grounded" },
]

export const SIGNAL_QUALITY_LABELS = [
    "No Signal",
    "Poor",
    "Fair",
    "Good",
    "Very Good",
    "Excellent",
] as const
export type ChecklistSubmissionWorkspaceVals = {
    userId: string
    workspaceName: string
    workspaceMembers: { id: string; fullName: string; isActive: boolean }[]
    workspaceAircrafts: { id: string; model: string | null; nickname: string | null }[]
}

export const MAX_MISSION_TAG_LENGTH_CHARS = 30

export type CreateAgoraLiveStreamMobileRequestData = {
    layerName?: string
    saveStream?: boolean
    location?: Coordinate
    sourceType?: LiveStreamType
}

export enum LiveStreamType {
    Drone = "drone",
    User = "user",
    MMA = "mma",
}

export type LiveVideoMetadata = {
    workspaceId: string
    projectId: string
    flightId: string
    region: SupportedRegions

    createdBy: { userId: string }
    saveRecording: boolean
    licenseId?: string
    live: boolean
    ended: boolean
    startedAt?: number
    isMobile?: boolean
    isAtlas?: boolean
    sourceType: LiveStreamType
    name: string
    pilotInitialCoordinate?: Coordinate
    pilotName?: string

    hasProcessedLeaveChannelEvent?: boolean
    hasProcessedRecordingEvent?: boolean

    // Handle needed to stop the cloud recording; Agora only returns it at start time.
    recording?: {
        resourceId: string
        sid: string
        uid: string
        mode: "individual" | "mix"
    }

    missionGroupIds?: string[]
}

export type Stream = {
    id: string
    name: string
    pilotName?: string
    createdAt: number | undefined
    disconnected?: boolean
}

export const USER_LOCATION_DISCONNECT_GRACE_PERIOD_MS = 30_000

export const FLIGHT_RECORD_PROCESSOR_CONCURRENCY = 3

export const RECENT_SEARCHES_PREVIEW_COUNT = 5
export const MAX_RECENT_SEARCHES = 20

export type AirspaceFeatureType = "tfr" | "restriction" | "notam"
export function isValidAirspaceType(t: unknown): t is AirspaceFeatureType {
    return typeof t === "string" && (t === "tfr" || t === "restriction" || t === "notam")
}
export type AirspaceFeatureStatus = "active" | "scheduled" | "permanent"
export const AIRSPACE_NOTAM_TYPES = [
    "RWY",
    "NAV",
    "TXY",
    "IAP",
    "LGT",
    "OBST",
    "OBST_LGT",
    "OTHER",
] as const
export type AirspaceNotamType = (typeof AIRSPACE_NOTAM_TYPES)[number]

export const AIRSPACE_TFR_TYPES = ["AS", "HAZ", "SEC", "spcOps", "SPEC", "uasPg", "VIP"] as const
export type AirspaceTfrType = (typeof AIRSPACE_TFR_TYPES)[number]

export const USA_AIRSPACE_RESTRICTION_CATEGORIES = [
    "B",
    "C",
    "D",
    "MOA",
    "Restricted",
    "Prohibited",
    "Other",
] as const
export type UsaAirspaceRestrictionCategory = (typeof USA_AIRSPACE_RESTRICTION_CATEGORIES)[number]

export const CANADIAN_AIRSPACE_RESTRICTION_CATEGORIES = [
    "CYR",
    "CYD",
    "CYA",
    "CZ",
    "TCA",
    "CAE",
    "TA",
    "tspRq",
    "awyVhf",
    "rnavRt",
    "awyLf",
    "rest",
] as const
export type CanadianAirspaceRestrictionCategory =
    (typeof CANADIAN_AIRSPACE_RESTRICTION_CATEGORIES)[number]

export type AirspaceRestrictionCategory =
    | UsaAirspaceRestrictionCategory
    | CanadianAirspaceRestrictionCategory

export type OpenAirAltitude =
    | { reference: "SFC" }
    | { reference: "UNLIMITED" }
    | { reference: "MSL" | "AGL"; feet: number }
    | { reference: "FL"; flightLevel: number }

export interface AirspaceFeatureProperties {
    id: string
    featureType: AirspaceFeatureType
    status: AirspaceFeatureStatus
    notamType?: AirspaceNotamType
    tfrType?: AirspaceTfrType
    title: string
    identifier: string
    location?: string
    startsAt?: string
    endsAt?: string | null
    altitudeLower?: OpenAirAltitude
    altitudeUpper?: OpenAirAltitude
    condition?: string
    reason?: string
    affects?: string
    radiusNm?: number
    rawText?: string
    rawLocalFormat?: string
    rawIcaoFormat?: string
    restrictionCategories?: AirspaceRestrictionCategory[]
    usingAgencyName?: string
    usingAgencyPhone?: string
    controllingAgencyName?: string
    controllingAgencyPhone?: string
}

export type PolygonWithHoles = {
    outer: Coordinate[]
    holes: Coordinate[][]
}

export type MultiPolygonWithHoles = PolygonWithHoles[]

export const AIRSPACE_SIMPLIFICATION_TOLERANCES = [
    0.05, 0.025, 0.012, 0.006, 0.003, 0.0015, 0.0007, 0.0003, 0.00015, 0,
]

export type AircraftSource = "adsb" | "spidertracks"
export const AIRCRAFT_TYPES = ["fixedWing", "rotorcraft", "lta", "glider", "other"] as const
export type AircraftType = (typeof AIRCRAFT_TYPES)[number]
export type AdsbDataSourceType = "ADSB" | "MLAT" | "UAT" | "ESTIMATED" | "unknown"
export type AdsbEmergencyType = "hijack" | "radioFailure" | "general"

export interface TrackedAircraft {
    id: string
    source: AircraftSource
    lat: number
    lng: number
    altitude: number
    heading: number
    speed: number
    timestamp: number
    type: AircraftType
    verticalRate: number
    callsign: string
}

export interface AdsbAircraftDetail {
    id: string
    callsign: string
    manufacturer: string | null
    model: string | null
    variant: string | null
    tailNumber: string | null
    operatorAirline: string | null
    lat: number
    lng: number
    altitude: number
    heading: number
    speed: number
    verticalRate: number
    timestamp: number
    squawk: string | null
    emergency: AdsbEmergencyType | null
    onGround: boolean
    dataSourceType: AdsbDataSourceType
}

export const ADSB_LABEL_MIN_ZOOM = 8
export const ADSB_AREA_RADIUS_METERS = 20_000 // SafeSky UAV API's max radius for this endpoint
export const ADSB_POLL_MS = 5_000
export const ADSB_DEAD_RECKONING_INTERVAL = 100

export const SPIDERTRACKS_POLL_MS = 30_000
export const SPIDERTRACKS_CACHE_TTL_SECONDS = SPIDERTRACKS_POLL_MS / 1000

export enum ContentViewMode {
    Grid = "grid",
    List = "list",
}

export enum ContentSortMode {
    Name = "name",
    DateModified = "date-modified",
    DateCreated = "date-created",
}

export enum ContentSortDirection {
    Ascending = "ascending",
    Descending = "descending",
}
